import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertCircle, ArrowRight } from "lucide-react";
import { Field, Input, PasswordInput } from "@/portal/ui/Field";
import { Button } from "@/portal/ui/Button";
import "@/portal/theme.css";

const HERO = "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800";
const BENEFITS = ["Status și evoluția lucrărilor", "Decizii, aprobări și registru complet", "Documente, rapoarte și galerie foto"];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErr, setFieldErr] = useState({});
  const [pos, setPos] = useState(BENEFITS.length); // start in middle copy for seamless loop
  const [animate, setAnimate] = useState(true);
  const year = new Date().getFullYear();

  const ROW = 52; // px per row
  const N = BENEFITS.length;
  const LOOP = [...BENEFITS, ...BENEFITS, ...BENEFITS]; // 3 copies for seamless vertical scroll

  useEffect(() => {
    const t = setInterval(() => setPos((p) => p + 1), 2300);
    return () => clearInterval(t);
  }, []);

  // Seamless wrap: after sliding into the last copy, jump back without animation
  useEffect(() => {
    if (pos >= 2 * N) {
      const id = setTimeout(() => { setAnimate(false); setPos((p) => p - N); }, 600);
      return () => clearTimeout(id);
    }
  }, [pos, N]);

  useEffect(() => {
    if (!animate) {
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
      return () => cancelAnimationFrame(id);
    }
  }, [animate]);

  async function onSubmit(e) {
    e.preventDefault();
    const fe = {};
    if (!email.trim()) fe.email = "Introdu adresa de email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) fe.email = "Adresa de email nu este validă.";
    if (!password) fe.password = "Introdu parola.";
    setFieldErr(fe); if (Object.keys(fe).length) return;
    setErr(""); setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (!res.ok) { setErr(res.error); return; }
    toast.success("Autentificare reușită");
    const from = location.state?.from?.pathname;
    if (from) return navigate(from, { replace: true });
    navigate(res.user.role === "client" ? "/portal" : "/staff", { replace: true });
  }

  return (
    <div className="cp min-h-screen grid lg:grid-cols-[1.15fr_1fr]">
      {/* Panou vizual */}
      <aside className="hidden lg:flex relative bg-cp-ink text-white overflow-hidden" aria-hidden="true">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.55]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14161A] via-[#14161A]/55 to-[#14161A]/20" />
        <div className="relative flex flex-col justify-center p-12 xl:p-16 w-full">
          <Link to="/" data-testid="login-back-home" className="absolute top-6 left-6 xl:top-8 xl:left-8 inline-block w-fit"><img src="/brisk-logo-white.png" alt="Brisk" className="h-14 xl:h-16 w-auto" /></Link>
          <div className="max-w-xl">
            <p className="cp-label !text-white/55 mb-4">Portal de guvernanță a proiectelor</p>
            <h1 className="font-cpserif text-[44px] xl:text-[56px] leading-[1.05] tracking-[-0.015em] font-medium">Claritate și control asupra fiecărei investiții.</h1>
            <p className="mt-6 text-[16px] leading-relaxed text-white/70 max-w-md">Status, decizii, documente și evoluția lucrărilor — într-un singur loc, actualizat de echipa Brisk Group.</p>
          </div>
          <p className="absolute bottom-12 left-12 xl:bottom-16 xl:left-16 text-[12px] text-white/45 flex items-center">© {year} Brisk Group · Consulting & Development</p>
        </div>
      </aside>

      {/* Formular */}
      <main className="relative flex flex-col bg-cp-bg px-6 py-8 sm:px-12 lg:px-14 xl:px-20" style={{ backgroundImage: "repeating-linear-gradient(135deg, rgba(20,22,26,0.022) 0px, rgba(20,22,26,0.022) 1px, transparent 1px, transparent 30px)" }}>
        <div className="relative flex items-center justify-between h-10">
          <Link to="/" className="lg:hidden inline-block" aria-label="Brisk"><img src="/brisk-logo.png" alt="Brisk" className="h-8 w-auto" /></Link>
          <span className="hidden lg:block cp-label">Portal client & echipă</span>
        </div>

        <div className="relative flex-1 flex items-center justify-center py-12">
          <div className="w-full max-w-[440px] cp-rise">
            <div className="text-center">
              <h2 className="cp-h1">Autentificare</h2>
              <p className="cp-body mt-2">Autentifică-te pentru a accesa proiectele tale.</p>
            </div>

            <form onSubmit={onSubmit} noValidate className="mt-8 cp-panel p-6 sm:p-7 space-y-5 shadow-[var(--cp-shadow-sm)]" data-testid="login-form">
              <Field label="Email" error={fieldErr.email}>
                {(p) => <Input {...p} type="email" autoComplete="email" inputMode="email" placeholder="nume@compania.ro" value={email} onChange={(e) => { setEmail(e.target.value); setFieldErr({}); }} data-testid="login-email" autoFocus />}
              </Field>
              <Field label="Parolă" error={fieldErr.password} action={<Link to="/forgot-password" data-testid="login-forgot" className="text-[12.5px] font-medium text-cp-accent hover:underline">Ai uitat parola?</Link>}>
                {(p) => <PasswordInput {...p} autoComplete="current-password" placeholder="Introdu parola ta" value={password} onChange={(e) => { setPassword(e.target.value); setFieldErr({}); }} data-testid="login-password" />}
              </Field>

              {err && (
                <div role="alert" data-testid="login-error" className="flex items-start gap-2.5 rounded-[var(--cp-radius)] border border-cp-danger-border bg-cp-danger-soft px-4 py-3 text-[13.5px] text-cp-danger">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" /><span>{err}</span>
                </div>
              )}

              <Button type="submit" size="lg" loading={busy} className="w-full" data-testid="login-submit">
                {busy ? "Se verifică datele…" : <>Intră în portal <ArrowRight /></>}
              </Button>
            </form>

            <div className="mt-9" data-testid="login-benefits">
              <div className="relative overflow-hidden" style={{ height: `${ROW * 3}px` }}>
                {/* dungile fixe care încadrează textul central */}
                <span className="absolute left-0 right-0 border-t border-cp-border" style={{ top: `${ROW}px` }} aria-hidden="true" />
                <span className="absolute left-0 right-0 border-t border-cp-border" style={{ top: `${ROW * 2}px` }} aria-hidden="true" />
                {/* reel vertical */}
                <div
                  className="will-change-transform"
                  style={{
                    transform: `translateY(${-(pos - 1) * ROW}px)`,
                    transition: animate ? "transform 620ms cubic-bezier(0.2,0.7,0.2,1)" : "none",
                  }}
                >
                  {LOOP.map((b, i) => {
                    const active = i === pos;
                    return (
                      <div
                        key={i}
                        aria-hidden={active ? undefined : "true"}
                        className="flex items-center justify-center text-center transition-[opacity,color] duration-500"
                        style={{
                          height: `${ROW}px`,
                          opacity: active ? 1 : 0.28,
                          color: active ? "var(--cp-text)" : "var(--cp-text-3)",
                          fontSize: "14px",
                          letterSpacing: "0.01em",
                        }}
                      >
                        {b}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative flex flex-wrap items-center justify-between gap-2 h-5 cp-meta">
          <span className="lg:hidden">© {year} Brisk Group</span>
          <span className="hidden lg:inline">Ai nevoie de ajutor? <a href="mailto:suport@briskgrp.com" className="text-cp-text-2 hover:text-cp-accent">suport@briskgrp.com</a></span>
          <span className="flex items-center gap-3">
            <Link to="/termeni" className="hover:text-cp-text">Termeni și Condiții</Link>
            <a href="mailto:suport@briskgrp.com" className="lg:hidden hover:text-cp-text">Suport</a>
          </span>
        </div>
      </main>
    </div>
  );
}
